<template>
    <RaterChrome :max-width="600" :show-anonymous="false">
        <div v-if="loading" class="body-small text-valence-stone-500 pt-20">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss class="mt-12" />
        <template v-else-if="invite">
            <div class="declined">
                <div class="declined__badge" aria-hidden="true">
                    <i class="bi bi-check-lg" aria-hidden="true"></i>
                </div>
                <h1 class="display-serif text-valence-grey-850 leading-tight">
                    <span v-if="declined">You've declined this request</span>
                    <span v-else>This invite is no longer active</span>
                </h1>
                <p v-if="declined" class="body-small text-valence-stone-500 max-w-md">
                    We let
                    <template v-if="subjectFirstName">{{ subjectFirstName }}</template>
                    <template v-else>them</template>
                    know anonymously — they'll see that someone stepped out, but not who.
                </p>
                <p v-else class="body-small text-valence-stone-500 max-w-md">This feedback request has closed. No action is needed from you.</p>
                <div class="declined__actions">
                    <a :href="closeHref" class="base-btn-neutral base-btn-square base-btn-sm inline-flex items-center gap-2"> Close </a>
                </div>
            </div>
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import BaseNotification from "~vue/components/BaseNotification.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { computed, watch } from "vue";

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const { invite, loading, loadError } = useInviteLoader(props.invite_token);

watch(invite, (loaded) => {
    if (!loaded) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_DECLINED_VIEWED, {
        declined: loaded.state === "revoked",
    });
});

const declined = computed(() => invite.value?.state === "revoked");
const subjectFirstName = computed(() => invite.value?.subject_first_name ?? "");
const closeHref = computed(() => (invite.value?.is_authenticated ? "/" : "https://valence.co"));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.declined {
    @apply flex flex-col items-center text-center gap-3.5;
    padding: 40px 0 8px;
}

.declined__badge {
    @apply flex items-center justify-center text-valence-stone-500 bg-valence-cream-200;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 6px;
    font-size: 26px;
    animation: declinedFadeIn 0.35s ease-out both;
}

.declined__actions {
    @apply flex gap-2.5;
    margin-top: 18px;
}

@keyframes declinedFadeIn {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .declined__badge {
        animation: none;
    }
}
</style>
